<div class="relative flex flex-col min-w-0 overflow-hidden">
  
  <!-- Loading State -->
  <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-32">
    <mat-spinner></mat-spinner>
    <div class="mt-4 text-lg text-gray-500">Ładowanie planów...</div>
  </div>

  <!-- Header & Cards -->
  <div *ngIf="!isLoading" class="relative pt-8 pb-12 sm:pt-16 sm:pb-24 px-4 sm:px-8 overflow-hidden">
    
    <!-- Background decoration -->
    <div class="absolute inset-0 -z-10 pointer-events-none overflow-hidden">
      <div class="absolute -top-24 -right-24 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
      <div class="absolute -bottom-24 -left-24 w-96 h-96 bg-primary-500/5 rounded-full blur-3xl"></div>
    </div>

    <!-- Alert o wygasłej subskrypcji -->
    <div *ngIf="isExpiredRedirect || isSubscriptionExpired" class="max-w-3xl mx-auto mb-8">
      <div class="flex items-start gap-4 p-5 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 shadow-lg">
        <div class="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
          <mat-icon [svgIcon]="'tabler_outline:alert-triangle'" class="text-white !w-6 !h-6"></mat-icon>
        </div>
        <div class="flex-1">
          <h3 class="text-lg font-bold text-amber-800 dark:text-amber-300">
            Twoja subskrypcja wygasła
          </h3>
          <p class="mt-1 text-amber-700 dark:text-amber-400">
            Odnów plan, aby kontynuować korzystanie z pełnej funkcjonalności aplikacji. 
            Wybierz jeden z dostępnych planów poniżej.
          </p>
        </div>
      </div>
    </div>

    <!-- Header -->
    <div class="flex flex-col items-center text-center mb-12">
      <h2 class="text-xl font-semibold uppercase dark:text-primary-400 tracking-wider">Cennik</h2>
      <div class="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
        <ng-container *ngIf="isExpiredRedirect || isSubscriptionExpired; else defaultTitle">
          Odnów swój plan
        </ng-container>
        <ng-template #defaultTitle>
          Wybierz plan dla siebie
        </ng-template>
      </div>
      <div class="mt-4 text-lg text-gray-500 dark:text-gray-400 max-w-2xl">
        <ng-container *ngIf="isExpiredRedirect || isSubscriptionExpired; else defaultSubtitle">
          Twoja subskrypcja wygasła. Wybierz plan, aby odblokować wszystkie funkcje.
        </ng-container>
        <ng-template #defaultSubtitle>
          Zacznij za darmo lub wybierz plan z pełnym dostępem do wszystkich funkcji
        </ng-template>
      </div>
    </div>

    <!-- Brak planów -->
    <div *ngIf="plans.length === 0" class="text-center py-16">
      <mat-icon [svgIcon]="'tabler_outline:mood-empty'" class="icon-size-16 text-gray-300 mb-4"></mat-icon>
      <p class="text-gray-500 dark:text-gray-400">Brak dostępnych planów subskrypcji.</p>
      <p class="text-sm text-gray-400 mt-2">Skontaktuj się z administratorem.</p>
    </div>

    <!-- Plans Grid -->
    <div *ngIf="plans.length > 0" class="flex flex-wrap justify-center gap-5 max-w-5xl mx-auto mb-16 relative z-10 items-stretch">
      
      <ng-container *ngFor="let plan of plans">
        <!-- FREE Plan -->
        <div *ngIf="plan.stage === 0"
          id="plan-0"
          class="relative flex flex-col bg-card rounded-2xl shadow-md border overflow-hidden transition-all hover:shadow-lg w-full md:w-72 lg:w-80"
          [ngClass]="isPlanHighlighted(0) ? 'border-gray-500 ring-4 ring-gray-500/30 scale-105' : 'border-gray-200 dark:border-gray-700'">
          
          <!-- Header spacer (dla wyrównania z START który ma badge) -->
          <div class="h-9 bg-gray-50 dark:bg-gray-800/50"></div>

          <div class="p-6 text-center">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <mat-icon [svgIcon]="'tabler_outline:sparkles'" class="text-gray-500 icon-size-6"></mat-icon>
            </div>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ plan.name }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 min-h-[40px]">{{ plan.description || 'Na start' }}</p>
          </div>

          <div class="px-6 pb-4 text-center">
            <div class="flex items-baseline justify-center">
              <span class="text-4xl font-extrabold text-gray-900 dark:text-white">0</span>
              <span class="text-lg text-gray-500 ml-1">zł</span>
              <span class="text-sm text-gray-400 ml-1">/mies.</span>
            </div>
            <p class="text-sm text-gray-400 mt-1 min-h-[24px]">na zawsze</p>
          </div>

          <div class="flex-1 px-6 pb-4 flex items-center justify-center">
            <div class="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 dark:bg-gray-800">
              <mat-icon [svgIcon]="'tabler_outline:file-invoice'" class="text-gray-500 icon-size-5"></mat-icon>
              <span class="text-sm font-medium text-gray-600 dark:text-gray-300">
                {{ plan.invoiceLimit || 3 }} {{ (plan.invoiceLimit || 3) === 1 ? 'dokument' : (plan.invoiceLimit || 3) < 5 ? 'dokumenty' : 'dokumentów' }} / miesiąc
              </span>
            </div>
          </div>

          <div class="p-6 pt-2 mt-auto">
            <!-- Placeholder gdy użytkownik ma aktywny FREE -->
            <div *ngIf="hasActiveFreePlan" class="h-14 flex items-center justify-center">
              <span class="flex items-center gap-2 text-sm text-gray-400">
                <mat-icon [svgIcon]="'tabler_outline:check'" class="icon-size-4 text-green-500"></mat-icon>
                Twój aktualny plan
              </span>
            </div>
            <!-- Placeholder gdy użytkownik ma aktywny płatny plan - nie może zejść na FREE -->
            <div *ngIf="hasActivePaidPlan" class="h-14"></div>
            <!-- Aktywuj FREE - tylko gdy nie ma żadnego aktywnego planu -->
            <button 
              *ngIf="!hasActiveFreePlan && !hasActivePaidPlan"
              class="group relative w-full h-14 inline-flex items-center justify-center text-base font-bold text-gray-700 dark:text-gray-200 transition-all duration-300 rounded-xl overflow-hidden
                     bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600
                     hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:to-gray-500
                     shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-100"
              (click)="selectFreePlan(plan)"
              [disabled]="isProcessing">
              <span class="relative z-10 flex items-center gap-2">
                <mat-spinner *ngIf="isProcessing" diameter="20"></mat-spinner>
                <ng-container *ngIf="!isProcessing">
                  <mat-icon [svgIcon]="'tabler_outline:sparkles'" class="icon-size-5"></mat-icon>
                  <span>Aktywuj za darmo</span>
                </ng-container>
              </span>
              <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
            </button>
          </div>
        </div>

        <!-- START Plan (Popular) -->
        <div *ngIf="plan.stage === 1"
          id="plan-1"
          class="relative flex flex-col bg-card rounded-2xl shadow-xl border-2 overflow-hidden transition-all hover:shadow-2xl w-full md:w-72 lg:w-80"
          [ngClass]="isPlanHighlighted(1) ? 'border-primary-500 ring-4 ring-primary-500/30 scale-105' : 'border-primary-500'">
          
          <!-- Discount badge w prawym górnym rogu -->
          <div *ngIf="hasDiscount(plan)" 
            class="absolute top-12 right-0 z-10 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-1.5 rounded-l-full shadow-lg flex items-center gap-1.5 animate-pulse">
            <mat-icon [svgIcon]="'tabler_outline:tag'" class="icon-size-4 text-white"></mat-icon>
            <span class="font-bold text-sm">-{{ plan.discountPercent }}%</span>
          </div>

          <!-- Popular badge -->
          <div class="h-9 bg-gradient-to-r from-primary-500 to-primary-600 text-white flex items-center justify-center text-sm font-semibold uppercase tracking-wider">
            🔥 Najpopularniejszy
          </div>

          <div class="p-6 text-center">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <mat-icon [svgIcon]="'tabler_outline:rocket'" class="text-primary-600 dark:text-primary-400 icon-size-6"></mat-icon>
            </div>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ plan.name }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 min-h-[40px]">{{ plan.description || 'Dla małych firm' }}</p>
          </div>

          <div class="px-6 pb-4 text-center">
            <div class="flex items-baseline justify-center gap-2">
              <span *ngIf="hasDiscount(plan)" class="text-xl text-gray-400 line-through">
                {{ formatPrice(getMonthlyPrice(plan, true)) }} zł
              </span>
              <span class="text-4xl font-extrabold text-primary-600">{{ formatPrice(getMonthlyPrice(plan)) }}</span>
              <span class="text-lg text-gray-500">zł</span>
              <span class="text-sm text-gray-400">/mies.</span>
            </div>
            <p class="text-sm text-gray-500 mt-2 min-h-[24px]">
              płatność roczna 
              <span *ngIf="hasDiscount(plan)" class="line-through text-gray-400 mr-1">{{ formatPrice(plan.originalPrice!) }} zł</span>
              <span class="font-semibold">{{ formatPrice(plan.price) }} zł brutto</span>
            </p>
          </div>

          <div class="flex-1 px-6 pb-4 flex items-center justify-center">
            <div class="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-900/20">
              <mat-icon [svgIcon]="'tabler_outline:infinity'" class="text-primary-600 dark:text-primary-400 icon-size-5"></mat-icon>
              <span class="text-sm font-medium text-primary-700 dark:text-primary-300">
                {{ plan.invoiceLimit ? plan.invoiceLimit + ' dokumentów / miesiąc' : 'Bez limitu dokumentów' }}
              </span>
            </div>
          </div>

          <div class="p-6 pt-2 mt-auto">
            <button 
              class="group relative w-full h-14 inline-flex items-center justify-center text-base font-bold text-white transition-all duration-300 rounded-xl overflow-hidden
                     bg-gradient-to-r from-primary-500 via-primary-600 to-blue-600
                     hover:from-primary-400 hover:via-primary-500 hover:to-blue-500
                     shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/40
                     hover:scale-[1.02] active:scale-100"
              (click)="selectPlan(plan)">
              <span class="relative z-10 flex items-center gap-2">
                <mat-icon [svgIcon]="'tabler_outline:rocket'" class="icon-size-5 text-white"></mat-icon>
                <span>Wybieram START</span>
                <mat-icon [svgIcon]="'tabler_outline:arrow-right'" class="icon-size-5 text-white transition-transform group-hover:translate-x-1"></mat-icon>
              </span>
              <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
            </button>
          </div>
        </div>

        <!-- PRO Plan -->
        <div *ngIf="plan.stage === 2"
          id="plan-2"
          class="relative flex flex-col bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-lg overflow-hidden transition-all hover:shadow-xl w-full md:w-72 lg:w-80"
          [ngClass]="isPlanHighlighted(2) ? 'ring-4 ring-amber-500/50 scale-105' : ''">
          
          <!-- Discount badge w prawym górnym rogu -->
          <div *ngIf="hasDiscount(plan)" 
            class="absolute top-12 right-0 z-10 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1.5 rounded-l-full shadow-lg flex items-center gap-1.5 animate-pulse">
            <mat-icon [svgIcon]="'tabler_outline:tag'" class="icon-size-4 text-white"></mat-icon>
            <span class="font-bold text-sm text-white">-{{ plan.discountPercent }}%</span>
          </div>

          <!-- Header spacer -->
          <div class="h-9 bg-gray-800/50"></div>

          <div class="p-6 text-center">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <mat-icon [svgIcon]="'tabler_outline:crown'" class="text-amber-400 icon-size-6"></mat-icon>
            </div>
            <h3 class="text-xl font-bold text-white">{{ plan.name }}</h3>
            <p class="text-sm text-gray-400 mt-1 min-h-[40px]">{{ plan.description || 'Dla profesjonalistów' }}</p>
          </div>

          <div class="px-6 pb-4 text-center">
            <div class="flex items-baseline justify-center gap-2">
              <span *ngIf="hasDiscount(plan)" class="text-xl text-gray-500 line-through">
                {{ formatPrice(getMonthlyPrice(plan, true)) }} zł
              </span>
              <span class="text-4xl font-extrabold text-white">{{ formatPrice(getMonthlyPrice(plan)) }}</span>
              <span class="text-lg text-gray-400">zł</span>
              <span class="text-sm text-gray-500">/mies.</span>
            </div>
            <p class="text-sm text-gray-400 mt-2 min-h-[24px]">
              płatność roczna 
              <span *ngIf="hasDiscount(plan)" class="line-through text-gray-500 mr-1">{{ formatPrice(plan.originalPrice!) }} zł</span>
              <span class="font-semibold text-white">{{ formatPrice(plan.price) }} zł brutto</span>
            </p>
          </div>

          <div class="flex-1 px-6 pb-4 flex items-center justify-center">
            <div class="flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10">
              <mat-icon [svgIcon]="'tabler_outline:infinity'" class="text-amber-400 icon-size-5"></mat-icon>
              <span class="text-sm font-medium text-amber-300">
                {{ plan.invoiceLimit ? plan.invoiceLimit + ' dokumentów / miesiąc' : 'Bez limitu dokumentów' }}
              </span>
            </div>
          </div>

          <div class="p-6 pt-2 mt-auto">
            <button 
              class="group relative w-full h-14 inline-flex items-center justify-center text-base font-bold text-gray-900 transition-all duration-300 rounded-xl overflow-hidden
                     bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500
                     hover:from-amber-300 hover:via-amber-400 hover:to-orange-400
                     shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/40
                     hover:scale-[1.02] active:scale-100"
              (click)="selectPlan(plan)">
              <span class="relative z-10 flex items-center gap-2">
                <mat-icon [svgIcon]="'tabler_outline:crown'" class="icon-size-5 text-gray-900"></mat-icon>
                <span>Wybieram PRO</span>
                <mat-icon [svgIcon]="'tabler_outline:arrow-right'" class="icon-size-5 text-gray-900 transition-transform group-hover:translate-x-1"></mat-icon>
              </span>
              <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
            </button>
          </div>
        </div>

        <!-- BUSINESS Plan (future) -->
        <div *ngIf="plan.stage === 3"
          id="plan-3"
          class="relative flex flex-col bg-card rounded-2xl shadow-md border overflow-hidden transition-all hover:shadow-lg w-full md:w-72 lg:w-80"
          [ngClass]="isPlanHighlighted(3) ? 'border-purple-500 ring-4 ring-purple-500/30 scale-105' : 'border-gray-200 dark:border-gray-700'">
          <div class="h-9 bg-purple-50 dark:bg-purple-900/20"></div>
          <div class="p-6 text-center">
            <div class="w-12 h-12 mx-auto mb-3 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <mat-icon [svgIcon]="'tabler_outline:building-skyscraper'" class="text-purple-600 dark:text-purple-400 icon-size-6"></mat-icon>
            </div>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ plan.name }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 min-h-[40px]">{{ plan.description }}</p>
          </div>
          <div class="px-6 pb-4 text-center">
            <div class="flex items-baseline justify-center">
              <span class="text-4xl font-extrabold text-gray-900 dark:text-white">{{ formatPrice(getMonthlyPrice(plan)) }}</span>
              <span class="text-lg text-gray-500 ml-1">zł</span>
              <span class="text-sm text-gray-400 ml-1">/mies.</span>
            </div>
            <p class="text-sm text-gray-500 mt-1 min-h-[24px]">płatność roczna {{ formatPrice(plan.price) }} zł brutto</p>
          </div>
          <div class="flex-1 px-6 pb-4 flex items-center justify-center">
            <div class="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 dark:bg-purple-900/20">
              <mat-icon [svgIcon]="'tabler_outline:infinity'" class="text-purple-600 dark:text-purple-400 icon-size-5"></mat-icon>
              <span class="text-sm font-medium text-purple-700 dark:text-purple-300">
                {{ plan.invoiceLimit ? plan.invoiceLimit + ' dokumentów / miesiąc' : 'Bez limitu dokumentów' }}
              </span>
            </div>
          </div>
          <div class="p-6 pt-2 mt-auto">
            <button 
              class="group relative w-full h-14 inline-flex items-center justify-center text-base font-bold text-white transition-all duration-300 rounded-xl overflow-hidden
                     bg-gradient-to-r from-purple-500 via-purple-600 to-violet-600
                     hover:from-purple-400 hover:via-purple-500 hover:to-violet-500
                     shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/40
                     hover:scale-[1.02] active:scale-100"
              (click)="selectPlan(plan)">
              <span class="relative z-10 flex items-center gap-2">
                <mat-icon [svgIcon]="'tabler_outline:building-skyscraper'" class="icon-size-5 text-white"></mat-icon>
                <span>Wybieram BUSINESS</span>
                <mat-icon [svgIcon]="'tabler_outline:arrow-right'" class="icon-size-5 text-white transition-transform group-hover:translate-x-1"></mat-icon>
              </span>
              <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
            </button>
          </div>
        </div>
      </ng-container>

    </div>

    <!-- Features comparison table -->
    <div *ngIf="plans.length > 0 && features.length > 0" class="max-w-5xl mx-auto">
      <h3 class="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">
        Porównanie funkcjonalności
      </h3>
      
      <div class="bg-card rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="bg-gray-50 dark:bg-gray-800">
                <th class="text-left py-4 px-6 font-semibold text-gray-900 dark:text-white">Funkcjonalność</th>
                <th *ngIf="hasPlan(0)" class="text-center py-4 px-6 font-semibold text-gray-500">{{ getPlanByStage(0)?.name }}</th>
                <th *ngIf="hasPlan(1)" class="text-center py-4 px-6 font-semibold text-primary-600 bg-primary-50 dark:bg-primary-900/20">{{ getPlanByStage(1)?.name }}</th>
                <th *ngIf="hasPlan(2)" class="text-center py-4 px-6 font-semibold text-gray-900 dark:text-white">{{ getPlanByStage(2)?.name }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let feature of features; let odd = odd" 
                  [ngClass]="{'bg-gray-50/50 dark:bg-gray-800/50': odd}">
                <td class="py-3 px-6 text-gray-700 dark:text-gray-300">
                  {{ feature.name }}
                </td>
                <td *ngIf="hasPlan(0)" class="py-3 px-6 text-center">
                  <mat-icon *ngIf="feature.free === true" [svgIcon]="'tabler_outline:check'" class="text-gray-400 icon-size-5"></mat-icon>
                  <mat-icon *ngIf="feature.free === false" [svgIcon]="'tabler_outline:x'" class="text-red-400 dark:text-red-500 icon-size-5"></mat-icon>
                  <span *ngIf="feature.free !== true && feature.free !== false" class="text-sm font-medium text-gray-500">
                    {{ feature.free }}
                  </span>
                </td>
                <td *ngIf="hasPlan(1)" class="py-3 px-6 text-center bg-primary-50/50 dark:bg-primary-900/10">
                  <mat-icon *ngIf="feature.start === true" [svgIcon]="'tabler_outline:check'" class="text-primary-500 icon-size-5"></mat-icon>
                  <mat-icon *ngIf="feature.start === false" [svgIcon]="'tabler_outline:x'" class="text-red-400 dark:text-red-500 icon-size-5"></mat-icon>
                  <span *ngIf="feature.start !== true && feature.start !== false" class="text-sm font-medium text-primary-600">
                    {{ feature.start }}
                  </span>
                </td>
                <td *ngIf="hasPlan(2)" class="py-3 px-6 text-center">
                  <mat-icon *ngIf="feature.pro === true" [svgIcon]="'tabler_outline:check'" class="text-green-500 icon-size-5"></mat-icon>
                  <mat-icon *ngIf="feature.pro === false" [svgIcon]="'tabler_outline:x'" class="text-red-400 dark:text-red-500 icon-size-5"></mat-icon>
                  <span *ngIf="feature.pro !== true && feature.pro !== false" class="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {{ feature.pro }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- FAQ -->
    <div class="max-w-3xl mx-auto mt-16">
      <h3 class="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">Często zadawane pytania</h3>
      
      <div class="space-y-4">
        <!-- FAQ 1 -->
        <div class="bg-card rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <details class="group">
            <summary class="flex items-center justify-between cursor-pointer p-5 select-none">
              <span class="text-base font-semibold text-gray-900 dark:text-white">Czy mogę zmienić plan w trakcie trwania subskrypcji?</span>
              <mat-icon [svgIcon]="'tabler_outline:chevron-down'" class="icon-size-5 text-gray-400 transition-transform group-open:rotate-180"></mat-icon>
            </summary>
            <div class="px-5 pb-5 text-gray-600 dark:text-gray-400 text-base leading-relaxed">
              Tak! Możesz zmienić plan w dowolnym momencie. Przy przejściu na wyższy plan, pozostałe dni z obecnej subskrypcji 
              zostaną proporcjonalnie dodane do nowego okresu. Nowy plan aktywuje się natychmiast.
            </div>
          </details>
        </div>

        <!-- FAQ 2 -->
        <div class="bg-card rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <details class="group">
            <summary class="flex items-center justify-between cursor-pointer p-5 select-none">
              <span class="text-base font-semibold text-gray-900 dark:text-white">Jakie metody płatności akceptujecie?</span>
              <mat-icon [svgIcon]="'tabler_outline:chevron-down'" class="icon-size-5 text-gray-400 transition-transform group-open:rotate-180"></mat-icon>
            </summary>
            <div class="px-5 pb-5 text-gray-600 dark:text-gray-400 text-base leading-relaxed">
              Akceptujemy płatności kartą (Visa, Mastercard), BLIK, szybkie przelewy bankowe oraz tradycyjny przelew. 
              Wszystkie płatności online są obsługiwane przez certyfikowanego operatora PayU.
            </div>
          </details>
        </div>

        <!-- FAQ 3 -->
        <div class="bg-card rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <details class="group">
            <summary class="flex items-center justify-between cursor-pointer p-5 select-none">
              <span class="text-base font-semibold text-gray-900 dark:text-white">Czy otrzymam fakturę za subskrypcję?</span>
              <mat-icon [svgIcon]="'tabler_outline:chevron-down'" class="icon-size-5 text-gray-400 transition-transform group-open:rotate-180"></mat-icon>
            </summary>
            <div class="px-5 pb-5 text-gray-600 dark:text-gray-400 text-base leading-relaxed">
              Tak, po opłaceniu subskrypcji automatycznie wystawiamy fakturę VAT. Faktura jest dostępna do pobrania 
              w zakładce <strong>Moje konto → Subskrypcja</strong>.
            </div>
          </details>
        </div>

        <!-- FAQ 4 -->
        <div class="bg-card rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <details class="group">
            <summary class="flex items-center justify-between cursor-pointer p-5 select-none">
              <span class="text-base font-semibold text-gray-900 dark:text-white">Co się stanie gdy moja subskrypcja wygaśnie?</span>
              <mat-icon [svgIcon]="'tabler_outline:chevron-down'" class="icon-size-5 text-gray-400 transition-transform group-open:rotate-180"></mat-icon>
            </summary>
            <div class="px-5 pb-5 text-gray-600 dark:text-gray-400 text-base leading-relaxed">
              Po wygaśnięciu subskrypcji nie będziesz mógł tworzyć nowych dokumentów, a faktury z KSeF nie będą się synchronizowały. 
              Zachowasz jednak dostęp do wszystkich swoich danych. Możesz odnowić subskrypcję w dowolnym momencie, 
              aby odblokować pełną funkcjonalność.
            </div>
          </details>
        </div>

        <!-- FAQ 5 -->
        <div class="bg-card rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <details class="group">
            <summary class="flex items-center justify-between cursor-pointer p-5 select-none">
              <span class="text-base font-semibold text-gray-900 dark:text-white">Potrzebuję więcej kont dla mojego zespołu. Czy macie ofertę firmową?</span>
              <mat-icon [svgIcon]="'tabler_outline:chevron-down'" class="icon-size-5 text-gray-400 transition-transform group-open:rotate-180"></mat-icon>
            </summary>
            <div class="px-5 pb-5 text-gray-600 dark:text-gray-400 text-base leading-relaxed">
              Oczywiście! Jeśli potrzebujesz wielu kont lub masz specyficzne wymagania, przygotujemy <strong>indywidualną ofertę</strong> dostosowaną 
              do potrzeb Twojej firmy. Napisz do nas na <a href="mailto:kontakt@fakturnia.pl" class="text-primary-600 hover:underline">kontakt&#64;fakturnia.pl</a> 
              — odpowiadamy w ciągu 24h.
            </div>
          </details>
        </div>
      </div>
    </div>

    <!-- Trust signals -->
    <div class="max-w-3xl mx-auto mt-16">
      <div class="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
        
        <!-- Bezpieczeństwo -->
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <mat-icon [svgIcon]="'tabler_outline:shield-check'" class="text-green-600 dark:text-green-400 icon-size-5"></mat-icon>
          </div>
          <span class="text-sm font-medium text-green-700 dark:text-green-400">Bezpieczne płatności</span>
        </div>

        <!-- Wsparcie -->
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <mat-icon [svgIcon]="'tabler_outline:headset'" class="text-purple-600 dark:text-purple-400 icon-size-5"></mat-icon>
          </div>
          <span class="text-sm font-medium text-purple-700 dark:text-purple-400">Wsparcie 24h</span>
        </div>

      </div>
    </div>

    <!-- CTA kontakt -->
    <div class="max-w-2xl mx-auto mt-16">
      <div class="relative bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-2xl border border-primary-200 dark:border-primary-800 p-8 text-center overflow-hidden">
        <div class="absolute -top-12 -right-12 w-40 h-40 bg-primary-500/5 rounded-full blur-2xl"></div>
        <div class="relative z-10">
          <div class="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
            <mat-icon [svgIcon]="'tabler_outline:messages'" class="text-primary-600 dark:text-primary-400 icon-size-7"></mat-icon>
          </div>
          <h4 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Potrzebujesz indywidualnej oferty?</h4>
          <p class="text-gray-600 dark:text-gray-400 mb-1">
            Masz wiele firm, potrzebujesz kont dla zespołu lub masz specyficzne wymagania?
          </p>
          <p class="text-gray-600 dark:text-gray-400 mb-6">
            Skontaktuj się z nami — przygotujemy ofertę szytą na miarę.
          </p>
          <a href="mailto:kontakt@fakturnia.pl?subject=Indywidualna%20oferta" 
            title="Indywidualna oferta"
            class="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-500 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/40">
            <mat-icon [svgIcon]="'tabler_outline:mail'" class="icon-size-5 text-white"></mat-icon>
            kontakt&#64;fakturnia.pl
          </a>
        </div>
      </div>
    </div>

    <!-- Info o cenach -->
    <div class="max-w-3xl mx-auto mt-12 text-center">
      <p class="text-xs text-gray-400 dark:text-gray-500">
        * Wszystkie ceny są cenami brutto (zawierają 23% VAT)
      </p>
    </div>
  </div>
</div>
